import { ReactNode } from 'react'
import { Eye, Flame, BadgeCheck, Heart, Pencil } from 'lucide-react'
import type { CategoryStyle } from '../lib/categoryColors'

const statusConfig: Record<string, { color: string; label: string }> = {
  running: { color: 'bg-green-500', label: 'Live' },
  stopped: { color: 'bg-gray-400', label: 'Stopped' },
  starting: { color: 'bg-yellow-500', label: 'Starting' },
  error: { color: 'bg-red-500', label: 'Error' },
}

export interface BrowseCardApp {
  name: string
  display_name: string | null
  description: string | null
  status: string
  url: string
  is_featured?: boolean
  unique_visitors_30d?: number
  visit_count_30d?: number
  owner_name?: string | null
  owner_email?: string | null
  is_favorited?: boolean
  my_permission?: string | null
}

interface CardProps<T extends BrowseCardApp> {
  app: T
  catCfg?: CategoryStyle | null
  onToggleFavorite?: (app: T) => void
  badges?: ReactNode
  bottomBadges?: ReactNode
  actions?: ReactNode
  menu?: ReactNode
}

export function BrowseGridCard<T extends BrowseCardApp>({
  app, catCfg, onToggleFavorite, badges, bottomBadges, actions, menu,
}: CardProps<T>) {
  const hasAccess = app.my_permission != null
  const isPopular = !app.is_featured && (app.unique_visitors_30d || 0) >= 3

  return (
    <div
      className={`p-3.5 rounded-lg border border-border bg-card transition-colors flex flex-col ${
        hasAccess ? 'hover:border-primary/40 cursor-pointer' : 'hover:border-border/80'
      }`}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('button, a')) return
        if (hasAccess && app.url) window.open(app.url, '_blank', 'noopener,noreferrer')
      }}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1 min-w-0">
          {app.is_featured && <FeaturedIcon />}
          {isPopular && <PopularIcon />}
          {catCfg && (
            <span className={`text-[11px] ${catCfg.text} truncate`}>#{catCfg.label}</span>
          )}
          {badges}
        </div>
        <div className="flex items-center gap-1.5">
          {onToggleFavorite && hasAccess && (
            <FavoriteButton app={app} onToggle={onToggleFavorite} />
          )}
          {menu}
          {actions}
        </div>
      </div>

      <h3 className="font-semibold text-sm truncate mb-0.5">
        {app.display_name || app.name}
      </h3>

      <p className="text-xs text-muted-foreground line-clamp-2 mb-3 flex-1">
        {app.description || 'No description'}
      </p>

      <div className="flex items-center justify-between pt-2 border-t border-border">
        <div className="flex items-center gap-2.5 flex-wrap min-w-0">
          <StatusBadge status={app.status} />
          <span className="inline-flex items-center gap-0.5 text-xs text-muted-foreground">
            <Eye className="w-3 h-3" />
            {app.visit_count_30d || 0}
          </span>
          {bottomBadges}
        </div>
        <AvatarWithTooltip name={app.owner_name} email={app.owner_email} />
      </div>
    </div>
  )
}

export function BrowseListCard<T extends BrowseCardApp>({
  app, catCfg, onToggleFavorite, badges, bottomBadges, actions, menu,
}: CardProps<T>) {
  const hasAccess = app.my_permission != null
  const isPopular = !app.is_featured && (app.unique_visitors_30d || 0) >= 3

  return (
    <div
      className={`px-3.5 py-2.5 rounded-lg border border-border bg-card transition-colors ${
        hasAccess ? 'hover:border-primary/40 cursor-pointer' : ''
      }`}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('button, a')) return
        if (hasAccess && app.url) window.open(app.url, '_blank', 'noopener,noreferrer')
      }}
    >
      <div className="flex items-center gap-3">
        {onToggleFavorite && hasAccess && (
          <FavoriteButton app={app} onToggle={onToggleFavorite} />
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold truncate">
              {app.display_name || app.name}
            </h3>
            <StatusBadge status={app.status} />
            {app.is_featured && <FeaturedIcon />}
            {isPopular && <PopularIcon />}
            {catCfg && (
              <span className={`text-[11px] ${catCfg.text}`}>#{catCfg.label}</span>
            )}
            {badges}
          </div>
          {app.description && (
            <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
              {app.description}
            </p>
          )}
        </div>

        <div className="flex items-center gap-3 flex-shrink-0">
          <span className="inline-flex items-center gap-0.5 text-xs text-muted-foreground">
            <Eye className="w-3 h-3" />
            {app.visit_count_30d || 0}
          </span>
          {bottomBadges}
          <AvatarWithTooltip name={app.owner_name} email={app.owner_email} />
          {menu}
          {actions}
        </div>
      </div>
    </div>
  )
}

/* ---- Shared Sub-Components ---- */

export function FeaturedIcon() {
  return (
    <span className="text-amber-500" title="Featured">
      <BadgeCheck className="w-3.5 h-3.5" />
    </span>
  )
}

export function PopularIcon() {
  return (
    <span className="text-orange-500" title="Popular">
      <Flame className="w-3.5 h-3.5" />
    </span>
  )
}

export function StatusBadge({ status }: { status: string }) {
  const info = statusConfig[status] || statusConfig.stopped
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] ${
      status === 'running' ? 'text-green-500' :
      status === 'error' ? 'text-red-500' :
      'text-muted-foreground'
    }`}>
      <span className={`w-1.5 h-1.5 rounded-full ${info.color}`} />
      {info.label}
    </span>
  )
}

export function AvatarWithTooltip({ name, email }: { name?: string | null; email?: string | null }) {
  const displayName = name || email || '?'
  const initials = displayName.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)

  return (
    <div
      className="w-6 h-6 rounded-full bg-secondary flex items-center justify-center flex-shrink-0"
      title={name ? `${name}\n${email || ''}` : email || ''}
    >
      <span className="text-[10px] font-medium text-muted-foreground">{initials}</span>
    </div>
  )
}

export function FavoriteButton<T extends { is_favorited?: boolean }>(
  { app, onToggle }: { app: T; onToggle: (app: T) => void },
) {
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onToggle(app) }}
      className={`p-1 rounded transition-colors ${
        app.is_favorited
          ? 'text-yellow-500 hover:text-yellow-600'
          : 'text-muted-foreground/40 hover:text-yellow-500'
      }`}
      title={app.is_favorited ? 'Remove from favorites' : 'Add to favorites'}
    >
      <Heart className="w-3.5 h-3.5" fill={app.is_favorited ? 'currentColor' : 'none'} />
    </button>
  )
}

export function PermissionBadge({ permission }: { permission: string }) {
  if (permission === 'edit' || permission === 'admin') {
    return (
      <span
        className="inline-flex items-center gap-0.5 text-[11px] text-blue-600 bg-blue-500/10 px-1.5 py-0.5 rounded"
        title="Can edit"
      >
        <Pencil className="w-2.5 h-2.5" />
        Editor
      </span>
    )
  }
  return (
    <span
      className="inline-flex items-center gap-0.5 text-[11px] text-muted-foreground bg-secondary px-1.5 py-0.5 rounded"
      title="View only"
    >
      <Eye className="w-2.5 h-2.5" />
      View Only
    </span>
  )
}
